import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Camera, Vote, Trophy, Clock, CheckCircle, ChevronDown } from 'lucide-react'
import { getCompetitionStatus } from '../lib/api'
import { Button } from '@/components/ui/Button'

type Phase = 'setup' | 'submission' | 'moderation' | 'voting' | 'closed'

export default function HomePage() {
  const [phase, setPhase] = useState<Phase | null>(null)
  const [_competitionName, setCompetitionName] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false)
  const howItWorksRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    async function loadStatus() {
      const result = await getCompetitionStatus()
      if (result.data) {
        setPhase(result.data.phase as Phase)
        setCompetitionName(result.data.name || 'Sierra Canyon Photography Competition')
      }
      setIsLoading(false)
    }
    loadStatus()
  }, [])

  return (
    <div className="bg-background flex min-h-[100dvh] flex-col">
      {/* Hero Section */}
      <section className="relative flex flex-1 flex-col overflow-hidden md:min-h-[400px]">
        {/* Gradient background - Sierra Canyon style */}
        <div className="absolute inset-0">
          {/* Base gradient */}
          <div className="from-navy/40 via-background to-background absolute inset-0 bg-gradient-to-b" />
          {/* Radial glow */}
          <div className="from-light-blue/20 absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] via-transparent to-transparent" />
          {/* Animated orbs */}
          <motion.div
            animate={{
              y: [-20, 20, -20],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="bg-navy absolute top-1/4 left-1/4 h-[600px] w-[600px] rounded-full blur-[120px]"
          />
          <motion.div
            animate={{
              y: [20, -20, 20],
              opacity: [0.08, 0.15, 0.08],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="bg-light-blue absolute right-1/4 bottom-1/4 h-[500px] w-[500px] rounded-full blur-[100px]"
          />
        </div>

        {/* SC Arts Competition Logo - top left on desktop, centered on mobile */}
        <div className="relative z-20 flex justify-center px-4 pt-4 md:justify-start">
          <div className="bg-foreground w-fit">
            <img src="/brand/SCS Arts Comp.png" alt="Sierra Canyon Arts Competition" className="h-16 w-auto md:h-24" />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-1 items-center justify-center py-6 md:pb-16">
          <div className="mx-auto max-w-4xl px-4 md:max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col md:flex-row md:items-center md:gap-8"
            >
              {/* Camera image - 1/3 width on desktop, hidden on mobile */}
              <div className="hidden items-center justify-center md:flex md:w-1/3">
                <img
                  src="/vector-vintage-camera.png"
                  alt="Vintage camera"
                  className="h-[30vw] max-h-[400px] w-[30vw] max-w-[400px] object-contain"
                />
              </div>

              {/* Text content - 2/3 width on desktop, left-aligned */}
              <div className="space-y-6 text-center md:mt-0 md:w-2/3 md:space-y-6 md:text-left">
                {/* Sierra Canyon horse logo */}
                <img
                  src="/brand/Horse_SolidBlue.png"
                  alt="Sierra Canyon School"
                  className="mx-auto h-12 w-auto drop-shadow-[0_0_6px_rgba(255,255,255,0.5)] md:mx-0 md:h-14"
                />
                <div className="space-y-1">
                  <h1 className="font-display text-4xl leading-tight font-bold tracking-tight md:text-6xl md:leading-[1.1] lg:text-7xl">
                    <span className="text-foreground">Sierra Canyon</span>
                    <span className="from-light-blue to-accent block bg-gradient-to-r bg-clip-text text-transparent">
                      Photo Competition
                    </span>
                  </h1>
                </div>

                {/* Phase actions (buttons) */}
                <div className="relative">
                  {isLoading ? (
                    <div className="flex h-16 items-center justify-center md:justify-start">
                      <div className="border-accent h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center md:justify-start">
                      <PhaseActions phase={phase} />
                    </div>
                  )}
                </div>

                {/* Camera image - mobile only, below button */}
                <div className="flex items-center justify-center md:hidden">
                  <img
                    src="/vector-vintage-camera.png"
                    alt="Vintage camera"
                    className="h-40 w-40 object-contain opacity-50"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works section */}
      <section className="border-border border-t px-4 py-4 md:py-6">
        {/* Mobile: clickable header with chevron */}
        <button
          onClick={() => {
            const newState = !isHowItWorksOpen
            setIsHowItWorksOpen(newState)
            // Scroll to bottom when opening on mobile
            if (newState) {
              setTimeout(() => {
                howItWorksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
              }, 350) // Wait for animation to complete
            }
          }}
          className="flex w-full items-center justify-center gap-2 md:hidden"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-foreground font-display text-center text-xl font-bold"
          >
            How It Works
          </motion.h2>
          <motion.div animate={{ rotate: isHowItWorksOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="text-foreground-muted h-5 w-5" />
          </motion.div>
        </button>

        {/* Desktop: static header (always visible) */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-foreground font-display mb-10 hidden text-center text-3xl font-bold md:block"
        >
          How It Works
        </motion.h2>

        {/* Steps - collapsible on mobile, always visible on desktop */}
        <motion.div
          ref={howItWorksRef}
          initial={false}
          animate={{
            height: isHowItWorksOpen ? 'auto' : 0,
            opacity: isHowItWorksOpen ? 1 : 0,
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden md:!h-auto md:!opacity-100"
        >
          <div className="flex flex-col items-center gap-5 pt-4 md:flex-row md:justify-center md:gap-4 md:pt-0">
            {[
              {
                icon: Camera,
                title: 'Submit',
                date: 'March 9th - 20th',
                description: 'Submissions open',
              },
              {
                icon: Vote,
                title: 'Vote',
                date: 'April 6th - 10th',
                description: 'Voting open',
              },
              {
                icon: Trophy,
                title: 'Win',
                date: 'April 20th - 24th',
                description: 'Winners announced during Arts Week',
              },
            ].map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex w-48 flex-col items-center text-center md:w-72"
              >
                {/* Icon + Heading row */}
                <div className="mb-1 flex items-center gap-2">
                  <div className="bg-surface border-border flex h-8 w-8 items-center justify-center rounded-lg border md:h-9 md:w-9">
                    <step.icon className="text-accent h-4 w-4 md:h-5 md:w-5" />
                  </div>
                  <h3 className="text-foreground text-base font-bold md:text-lg">{step.title}</h3>
                </div>
                {/* Date */}
                <p className="text-accent text-sm font-semibold md:text-base">{step.date}</p>
                {/* Description below */}
                <p className="text-foreground-muted text-xs md:text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>
    </div>
  )
}

function PhaseActions({ phase }: { phase: Phase | null }) {
  if (phase === 'setup') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="space-y-4">
        <div className="bg-accent/10 border-accent/20 inline-flex items-center gap-2 rounded-full border px-4 py-2">
          <Clock className="text-accent h-4 w-4" />
          <span className="text-accent text-sm font-medium">Coming Soon</span>
        </div>
      </motion.div>
    )
  }

  if (phase === 'submission') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Link to="/submit">
          <Button size="xl">
            <Camera className="h-5 w-5" />
            Submit Your Photo
          </Button>
        </Link>
      </motion.div>
    )
  }

  if (phase === 'moderation') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="space-y-4">
        <div className="bg-gold/10 border-gold/20 inline-flex items-center gap-2 rounded-full border px-4 py-2">
          <Clock className="text-accent h-4 w-4" />
          <span className="text-accent text-sm font-medium">Reviewing Submissions</span>
        </div>
        <p className="text-foreground-subtle">Voting opens soon!</p>
      </motion.div>
    )
  }

  if (phase === 'voting') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Link to="/vote">
          <Button size="xl">
            <Vote className="h-5 w-5" />
            Vote Now
          </Button>
        </Link>
      </motion.div>
    )
  }

  if (phase === 'closed') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="space-y-4">
        <div className="bg-success/10 border-success/20 inline-flex items-center gap-2 rounded-full border px-4 py-2">
          <CheckCircle className="text-success h-4 w-4" />
          <span className="text-success text-sm font-medium">Competition Complete</span>
        </div>
        <p className="text-foreground-subtle">Thank you for participating!</p>
      </motion.div>
    )
  }

  // Default fallback - show both buttons
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
      className="flex flex-col justify-center gap-4 sm:flex-row md:justify-start"
    >
      <Link to="/submit">
        <Button size="xl">
          <Camera className="h-5 w-5" />
          Submit Your Photo
        </Button>
      </Link>
      <Link to="/vote">
        <Button size="xl" variant="secondary">
          <Vote className="h-5 w-5" />
          Vote Now
        </Button>
      </Link>
    </motion.div>
  )
}
